import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'

import { DriverCalendarModel } from '@/app/models/calendar/driverCalendarModel'
import {
  mapActivity,
  mapExistingDelegation,
  mapOutOfScope,
  mapToActivityInfo,
} from '@/app/mappers/activityMappers'
import { ActivityInfo, GnssPositionModel } from '../types/activities'
import { TmsDataModel } from '@/app/models/tmsDataModel'

const useDriverActivityService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getDriverActivity = async ({
    companyId,
    driverId,
    startTime,
    endTime,
    includeTrips,
    includeVehicleTachographData,
  }: {
    companyId: string
    driverId: number
    startTime: string
    endTime: string
    includeTrips: boolean
    includeVehicleTachographData?: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      let url = `api/${companyId}/activity?&driverId=${driverId}&startTime=${startTime}&endTime=${endTime}&includeTrips=${includeTrips}`
      if (includeVehicleTachographData) {
        url += `&includeVehicleTachographData=true`
      }
      const res = await axiosInstance.get(url)
      const data: ActivityInfo = res.data

      const dataMapped = mapToActivityInfo(data)

      if (data.gnssPositions) {
        dataMapped.gnssPositions = (data.gnssPositions as any[]).map(
          (g): GnssPositionModel => ({
            timeStamp: moment.utc(g.timeStamp).local(),
            latitude: g.latitude,
            longitude: g.longitude,
            country: g.country,
            odometerValue: g.odometerValue,
          }),
        )
      }

      return dataMapped
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const getDriverTmsData = async ({
    companyId,
    driverId,
    startTime,
    endTime,
  }: {
    companyId: string
    driverId: number
    startTime: string
    endTime: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businessTrip/tmsData?&driverId=${driverId}&startTime=${startTime}&endTime=${endTime}`,
      )
      const data: TmsDataModel[] = res.data

      return data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getDriverCalendar = async ({
    companyId,
    driverId,
    year,
  }: {
    companyId: string
    driverId: string
    year: number
  }): Promise<DriverCalendarModel> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/driverCalendar/${driverId}/${year}`,
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const getAttestations = async (
    companyId: string,
    driverId: string,
    startTime: string,
    endTime: string,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/driverCalendar/attestations`,
        { params: { driverId, startTime, endTime } },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const changeDayNotes = async (
    companyId: string,
    driverId: string,
    date: string,
    dayType: number,
    notes: string,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/changeNotes`,
        { driverId, notes, dayType, date },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const changeDayTypes = async (
    companyId: string,
    driverId: string,
    dates: string[],
    dayType: number,
    notes: string,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/changeDayTypes`,
        { driverId, notes, dayType, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const restoreOriginalCardData = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/restoreOriginalCardData`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const removeDigitalData = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/removeDigitalData`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const removeBorderCrossings = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/removeBorderCrossings`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const removeTmsData = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/removeTmsData`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const assignBorderCrossingsFromVehicle = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/assignBorderCrossingsFromVehicle`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const autoAssignTmsDataToDriver = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/autoAssignTmsDataToDriver`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const copyActivitiesFromAnotherDay = async (
    companyId: string,
    selectedDriverId: string,
    selectedDates: string[],
    targetDriverId: string,
    targetDate: string,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/copyActivitiesFromAnotherDay`,
        { selectedDriverId, selectedDates, targetDriverId, targetDate },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const checkCopyActivityChance = async (
    companyId: string,
    selectedDriverId: string,
    selectedDates: string[],
    targetDriverId: string,
    targetDate: string,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/checkCopyActivityChance`,
        { selectedDriverId, selectedDates, targetDriverId, targetDate },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const checkAttestationExistence = async (
    companyId: string,
    driverId: string,
    dates: string[],
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/checkAttestationExistence`,
        { driverId, dates },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }

  const deleteAttestations = async (companyId: string, ids: string[]) => {
    const stopLoading = startLoading()
    try {
      const res = await Promise.all(
        ids.map((id) =>
          axiosInstance.post(
            `api/${companyId}/driverCalendar/deleteAttestation`,
            { id },
          ),
        ),
      )
      return res
    } finally {
      stopLoading()
    }
  }

  const addActivity = async (
    companyId: string,
    driverId: number,
    activity: any,
    addActivityMode: number,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`api/${companyId}/activity/add`, {
        driverId,
        activity,
        addActivityMode,
      })
      return res.data
    } catch (err) {
      console.error('Error adding activity:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const editActivity = async (
    companyId: string,
    driverId: number,
    activity: any,
  ) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`api/${companyId}/activity/edit`, {
        driverId,
        activity,
      })
      return res.data
    } catch (err) {
      console.error('Error editing activity:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const addPlace = async ({
    companyId,
    driverId,
    place,
  }: {
    companyId: string
    driverId: number
    place: {
      nationCode: number
      entryType: string
      startTime: string
      endTime: string
      odometerValue: number
    }
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/activity/addplace`,
        { driverId, place },
      )
      return res.data
    } catch (err) {
      console.error('Error adding place:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  return {
    getDriverActivity,
    getDriverCalendar,
    getAttestations,
    changeDayNotes,
    changeDayTypes,
    restoreOriginalCardData,
    removeDigitalData,
    removeBorderCrossings,
    getDriverTmsData,
    removeTmsData,
    assignBorderCrossingsFromVehicle,
    autoAssignTmsDataToDriver,
    copyActivitiesFromAnotherDay,
    checkCopyActivityChance,
    checkAttestationExistence,
    deleteAttestations,
    addActivity,
    editActivity,
    addPlace,
  }
}

export default useDriverActivityService
